<template>
  <div
    data-focus-view
    class="packages-container"
    :class="{ 'packages-container--affiliate': affiliateLayout }"
  >
    <nav class="package-breadcrumb tw:!flex tw:!items-center tw:!gap-2 tw:!text-sm tw:!text-gray-500 tw:!mb-4 tw:!whitespace-nowrap tw:!overflow-hidden">
      <a
        href="#"
        @click.prevent="$emit('back')"
        class="tw:!no-underline tw:!text-inherit tw:!transition-colors od-nav-hover"
      >
        {{ $t('nav.esimShop') }}
      </a>
      <ChevronRight />
      <span class="tw:!font-semibold tw:!overflow-hidden tw:!text-ellipsis od-nav-active">
        {{ store.countryName }}
      </span>
    </nav>

    <div class="compatibility-card tw:!bg-white tw:!rounded-3xl tw:!p-2 tw:!shadow-sm tw:!border tw:!border-gray-100 tw:!mb-4 tw:!box-border tw:!w-full tw:!block">
      <div class="details-section">
        <details class="device-check-details group">
          <summary class="tw:!cursor-pointer tw:!rounded-full tw:!font-bold tw:!px-4 tw:!py-2 tw:!w-full tw:!text-left tw:!flex tw:!justify-between tw:!items-center tw:!transition-colors tw:!list-none tw:![&::-webkit-details-marker]:hidden od-button">
            <span>{{ $t('packages.deviceCompatibility') }}</span>
            <ChevronDown />
          </summary>

          <div class="tw:!mt-4 tw:!px-1">
            <div class="tw:!text-xs tw:!text-gray-600 tw:!mb-4">
              <p class="tw:!m-0">{{ $t('packages.deviceRequirements.intro') }}</p>
              <ul class="tw:!list-disc tw:!pl-5 tw:!mt-1">
                <li>{{ $t('packages.deviceRequirements.esimSupport') }}</li>
                <li>{{ $t('packages.deviceRequirements.notLocked') }}</li>
              </ul>
            </div>

            <div class="tw:!flex tw:!gap-2 tw:!mb-4">
              <button
                class="tw:!px-4 tw:!py-2 tw:!rounded-full tw:!font-bold tw:!text-sm tw:!transition-all tw:!bg-gray-100 tw:!text-gray-700 tw:!border-0 tw:!cursor-pointer"
                :class="store.activeDeviceTab === 'ios' ? 'od-tab-active' : ''"
                @click="store.activeDeviceTab = 'ios'"
              >
                iOS
              </button>
              <button
                class="tw:!px-4 tw:!py-2 tw:!rounded-full tw:!font-bold tw:!text-sm tw:!transition-all tw:!bg-gray-100 tw:!text-gray-700 tw:!border-0 tw:!cursor-pointer"
                :class="store.activeDeviceTab === 'android' ? 'od-tab-active' : ''"
                @click="store.activeDeviceTab = 'android'"
              >
                Android
              </button>
            </div>

            <div class="tw:!h-64 tw:!overflow-y-auto tw:!border tw:!border-gray-100 tw:!rounded-xl tw:!p-3 custom-scrollbar">
              <div v-if="store.activeDeviceTab === 'ios'">
                <div v-for="(models, brand) in devicesDB.ios" :key="brand" class="tw:!mb-4">
                  <h4 class="tw:!font-bold tw:!text-black tw:!mb-2 tw:!top-0 tw:!bg-white tw:!pt-1 tw:!uppercase tw:!text-xs tw:!italic">
                    {{ brand }}
                  </h4>
                  <ul class="tw:!list-none tw:!p-0 tw:!m-0">
                    <li
                      v-for="model in models"
                      :key="model"
                      class="tw:!text-sm tw:!text-gray-700 tw:!pl-2 tw:!border-b tw:!border-gray-50 tw:!pb-1 tw:!mb-1"
                    >
                      {{ model }}
                    </li>
                  </ul>
                </div>
              </div>
              <div v-else>
                <div v-for="(models, brand) in devicesDB.android" :key="brand" class="tw:!mb-4">
                  <h4 class="tw:!font-bold tw:!text-black tw:!mb-2 tw:!top-0 tw:!bg-white tw:!pt-1 tw:!uppercase tw:!text-xs tw:!italic">
                    {{ brand }}
                  </h4>
                  <ul class="tw:!list-none tw:!p-0 tw:!m-0">
                    <li
                      v-for="model in models"
                      :key="model"
                      class="tw:!text-sm tw:!text-gray-700 tw:!pl-2 tw:!border-b tw:!border-gray-50 tw:!pb-1 tw:!mb-1"
                    >
                      {{ model }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="tw:!mt-4 tw:!flex tw:!flex-col tw:!gap-4">
              <div class="tw:!flex tw:!items-start tw:!gap-2 tw:!text-xs">
                <CheckCheck style="width: 16px; height: 16px;" />
                <span class="tw:!text-gray-600" v-html="$t('packages.topUp')"></span>
              </div>
              <div class="tw:!flex tw:!items-start tw:!gap-2 tw:!text-xs">
                <CheckCheck style="width: 16px; height: 16px;" />
                <span class="tw:!text-gray-600">{{ $t('packages.packageStartNote') }}</span>
              </div>
            </div>
          </div>
        </details>
      </div>
    </div>

    <div class="card-container tw:!bg-white tw:!rounded-3xl tw:!p-2 tw:!shadow-sm tw:!border tw:!border-gray-100 tw:!box-border tw:!mx-auto">
      <div class="tw:!flex tw:!justify-center tw:!mb-4">
        <div class="tw:!border-b-2 tw:!border-black tw:!pb-2 tw:!w-max tw:!mx-auto">
          <h2 class="tw:!text-lg tw:!font-bold tw:!text-gray-900">Data Packages</h2>
        </div>
      </div>

      <div v-if="store.loading.packages" class="tw:!text-center tw:!py-8">
        <LoaderCircle class="tw:!w-8 tw:!h-8 tw:!animate-spin tw:!mx-auto od-spinner" />
      </div>

      <div v-else-if="store.packages.length === 0" class="tw:!text-center tw:!py-8 tw:!text-gray-500">
        No packages available for this location
      </div>

      <div v-else>
        <div class="tw:!flex tw:!items-end tw:!mb-0 tw:!ml-2">
          <button
            v-for="tab in packageTabs"
            :key="tab.slot"
            @click="store.activePackageTab = tab.slot"
            class="tw:!px-4 tw:!py-2 tw:!font-bold tw:!text-sm tw:!transition-all tw:!duration-300 tw:!border-t tw:!border-l tw:!border-r tw:!cursor-pointer"
            :class="store.activePackageTab === tab.slot 
              ? 'tw:!bg-white tw:!text-black tw:!rounded-t-2xl tw:!border-gray-200 tw:!shadow-[0_-4px_10px_rgba(0,0,0,0.05)] tw:!z-10 tw:!relative tw:!-mb-[1px] tw:!scale-110 tw:!origin-bottom' 
              : 'tw:!bg-gray-50 tw:!rounded-t-xl tw:!opacity-70 tw:!border-gray-200'"
          >
            <div class="tw:!flex tw:!flex-col tw:!items-center">
              {{ tab.label }}
              <div v-if="store.activePackageTab === tab.slot" class="tw:!h-1 tw:!w-4 tw:!rounded-full tw:!mt-1" style="background-color: var(--esim-primary);"></div>
            </div>
          </button>
        </div>

        <div class="tw:!flex tw:!flex-col tw:!gap-4 tw:!pr-2 custom-scrollbar">
          <div v-for="partition in packagesDay" :key="partition.duration">
            <h4 class="tw:!my-3 tw:!pl-2 tw:!ml-4 tw:!border-l-2 tw:!leading-none od-border-accent">
              {{ partition.duration }} {{ $t('checkout.field.days') }}
            </h4>

            <div class="tw:!flex tw:!flex-col tw:!gap-4">
              <div
                v-for="pkg in partition.list"
                :key="pkg.packageCode"
                class="package-card tw:!border tw:!border-gray-200 tw:!rounded-2xl tw:!p-5 tw:!bg-white tw:!mx-auto"
              >
                <div class="tw:!flex tw:!items-center tw:!justify-between tw:!gap-2">
                  <div class="tw:!flex tw:!items-center tw:!gap-3">
                    <span class="tw:!font-bold tw:!text-base tw:!text-gray-900 tw:!whitespace-nowrap">
                      {{ formatData(pkg.volume) }}
                    </span>
                    <span class="tw:!text-gray-300 tw:!text-sm">|</span>
                    <span class="tw:!text-base tw:!font-bold tw:!whitespace-nowrap od-price">
                      <Transition name="price-flash" mode="out-in">
                        <span :key="count[pkg.slug] || 1">
                          ${{ formatPrice(pkg.price * (count[pkg.slug] || 1)) }}
                        </span>
                      </Transition>
                    </span>
                  </div>

                  <div class="tw:!flex tw:!items-center tw:!gap-2">
                    <div class="tw:!relative tw:!flex tw:!items-center tw:!rounded-md tw:!bg-gray-100 tw:!px-2 tw:!py-1">
                      <ChevronsUpDown class="tw:!w-3 tw:!h-3 tw:!text-gray-500 tw:!pointer-events-none" />
                      <select
                        class="tw:!bg-transparent tw:!border-0 tw:!font-bold tw:!text-sm tw:!w-8 tw:!text-center tw:!cursor-pointer tw:!outline-none tw:!appearance-none"
                        :value="count[pkg.slug] || 1"
                        @change="count[pkg.slug] = Number(($event.target as HTMLSelectElement).value)"
                      >
                        <option v-for="n in 6" :key="n" :value="n">{{ n }}</option>
                      </select>
                    </div>
                    <button
                      class="od-button tw:!rounded-full tw:!font-bold tw:!py-2 tw:!px-5 tw:!text-sm tw:!border-0 tw:!cursor-pointer tw:!whitespace-nowrap"
                      @click="$emit('select-package', pkg, count[pkg.slug] || 1)"
                    >
                      {{ $t('button.buy') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { devicesDB } from '~/utility/data'
import { formatData, formatPrice } from '~/utility/helper'
import type { PackageList } from '~/composables/types/esimPackage'
import { ChevronRight, LoaderCircle, ChevronDown, CheckCheck, ChevronsUpDown } from 'lucide-vue-next'
import { useStore } from '~/composables/widgetMode'

const store = useStore()
const { t } = useI18n()

withDefaults(defineProps<{
  affiliateLayout?: boolean
}>(), {
  affiliateLayout: false,
})

defineEmits<{
  'back': []
  'select-package': [pkg: PackageList, count: number]
}>()

const packageTabs = computed(() => [
  { label: t('button.standard'), slot: 'standard' },
  { label: t('button.dailyLimit'), slot: 'dailyLimit' },
])

const count = ref<Record<string, number>>({})

const packagesDay = computed(() => {
  const list = store.currentPackages

  const partition = list.reduce((acc, pkg) => {
    const day = pkg.duration
    if (!acc[day]) acc[day] = []
    acc[day].push(pkg)
    return acc
  }, {} as Record<number, PackageList[]>)

  return Object.keys(partition)
    .map(day => ({
      duration: Number(day),
      list: partition[Number(day)]?.sort((a, b) => a.volume - b.volume)
    }))
    .sort((a, b) => a.duration - b.duration)
})
</script>

<style scoped>
.package-card {
  width: auto;
  min-width: 86%;
}

.card-container {
  width: 80%;
}

.packages-container--affiliate {
  display: flex;
  flex-direction: column;
}

.packages-container--affiliate .package-breadcrumb {
  order: 0;
}

.packages-container--affiliate .card-container {
  order: 1;
  width: 100%;
  padding: 0;
  border: 0;
  box-shadow: none;
}

.packages-container--affiliate .compatibility-card {
  order: 2;
  margin-top: 1rem;
  margin-bottom: 0;
}

@media (max-width: 768px) {
  .package-card {
    width: 100%;
  }

  .card-container {
    width: 100%;
  }
}

.pkg-price {
  color: var(--esim-primary);
}

.od-nav-active {
  color: var(--esim-primary, #facc15);
}

.price-flash-enter-active,
.price-flash-leave-active {
  transition: all 0.15s ease;
}

.price-flash-enter-from {
  opacity: 0;
  transform: translateY(-4px) scale(1.1);
  color: var(--esim-primary);
}

.price-flash-leave-to {
  opacity: 0;
  transform: translateY(4px) scale(0.9);
}
</style>
